import {HttpClient, HttpResponse} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import {
  APP_NAME_HEADER_KEY,
  Beneficiary,
  ClientAgentAffiliationTime,
  ConfirmationStep,
  Coverage,
  ENVIRONMENT,
  EnvironmentShared,
  HealthAnswers,
  IdDocument,
  IdDocumentType,
  MinifiedProposal,
  PaginatedResponse,
  PaginationParams,
  Proposal,
  ProviderId,
  UserInfo,
  UtmParams,
} from '@emma-labs/shared/typescript';
import {Observable} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {defaultOptions, handleHttpError, HttpOptionsBuilder, responseToObject} from '../http-utils';
import {getDateWithoutOffset} from '../utility-functions';

@Injectable({
  providedIn: 'root',
})
export class ProposalApiService {
  private readonly basePath: string;

  constructor(
    private readonly http: HttpClient,
    @Inject(ENVIRONMENT) private readonly environment: EnvironmentShared,
  ) {
    this.basePath = `${environment.msProposalApiBase}/proposals`;
  }

  create(
    sessionId: string,
    agentId: string,
    providerId: ProviderId,
    fnaId: string,
    submitter: UserInfo,
    healthAnswers: HealthAnswers,
  ): Observable<Proposal> {
    const options = defaultOptions(sessionId, {appName: this.environment.app}).buildOptions();
    const url = new URL(this.basePath);
    const body = {
      submitter,
      healthAnswers,
      providerId,
      fnaId,
      agentId,
    };

    if (healthAnswers.birthday) {
      body.healthAnswers.birthday = getDateWithoutOffset(healthAnswers.birthday);
    }

    return this.http.post(url.toString(), body, options).pipe(
      map((response) => responseToObject<Proposal>(response as HttpResponse<string>)),
      catchError(handleHttpError),
    );
  }

  createCopy(sessionId: string, proposalId: string): Observable<Proposal> {
    const options = defaultOptions(sessionId, {appName: this.environment.app}).buildOptions();
    const url = new URL(`${this.basePath}/${btoa(proposalId)}/copy`);

    return this.http.post(url.toString(), {}, options).pipe(
      map((response) => responseToObject<Proposal>(response as HttpResponse<string>)),
      catchError(handleHttpError),
    );
  }

  getByProviderId(sessionId: string, providerId: ProviderId, utmParams?: UtmParams): Observable<Proposal> {
    const options = defaultOptions(sessionId, {appName: this.environment.app, utmParams}).buildOptions();
    const url = new URL(`${this.basePath}/me`);
    url.searchParams.set('providerId', providerId);

    return this.http.get(url.toString(), options).pipe(
      map((response) => responseToObject<Proposal>(response as HttpResponse<string>)),
      catchError(handleHttpError),
    );
  }

  getById(sessionId: string, proposalId: string): Observable<Proposal> {
    const options = defaultOptions(sessionId, {appName: this.environment.app}).buildOptions();
    const url = new URL(`${this.basePath}/${btoa(proposalId)}`);

    return this.http.get(url.toString(), options).pipe(
      map((response) => responseToObject<Proposal>(response as HttpResponse<string>)),
      catchError(handleHttpError),
    );
  }

  getAll(sessionId: string, excludeSelfServe: boolean): Observable<MinifiedProposal[]> {
    const options = defaultOptions(sessionId, {appName: this.environment.app}).buildOptions();
    const url = new URL(this.basePath);

    if (excludeSelfServe) {
      url.searchParams.set('excludeSelfServe', 'true');
    }

    return this.http.get(url.toString(), options).pipe(
      map(
        (response) =>
          responseToObject<{
            proposals: MinifiedProposal[];
          }>(response as HttpResponse<string>).proposals,
      ),
      catchError(handleHttpError),
    );
  }

  getAllPaginated(
    sessionId: string,
    params: PaginationParams,
    excludeSelfServe: boolean,
  ): Observable<PaginatedResponse<MinifiedProposal>> {
    const options = defaultOptions(sessionId, {appName: this.environment.app}).buildOptions();
    const url = new URL(`${this.basePath}/paginated`);

    url.searchParams.set('page', params.page.toString());
    url.searchParams.set('limit', params.limit.toString());

    if (params.search) {
      url.searchParams.set('search', params.search);
    }

    if (params.advisorFilter) {
      url.searchParams.set('advisorFilter', params.advisorFilter);
    }

    if (params.statusFilter && params.statusFilter.length > 0) {
      url.searchParams.set('statusFilter', params.statusFilter.join(','));
    }

    if (excludeSelfServe) {
      url.searchParams.set('excludeSelfServe', 'true');
    }

    return this.http.get(url.toString(), options).pipe(
      map((response) =>
        responseToObject<PaginatedResponse<MinifiedProposal>>(response as HttpResponse<string>),
      ),
      catchError(handleHttpError),
    );
  }

  search(sessionId: string, filter: string, excludePrimaryAgentHuman: boolean): Observable<Proposal[]> {
    const options = defaultOptions(sessionId, {appName: this.environment.app}).buildOptions();
    const url = new URL(`${this.basePath}/search`);
    url.searchParams.set('filter', filter);

    if (excludePrimaryAgentHuman) {
      url.searchParams.set('excludePrimaryAgentHuman', 'true');
    }

    return this.http.get(url.toString(), options).pipe(
      map(
        (response) => responseToObject<{proposals: Proposal[]}>(response as HttpResponse<string>).proposals,
      ),
      catchError(handleHttpError),
    );
  }

  getIdDocumentType(sessionId: string, proposalId: string, agentId: string): Observable<IdDocumentType> {
    const options = defaultOptions(sessionId, {appName: this.environment.app}).buildOptions();
    const url = new URL(`${this.basePath}/${btoa(proposalId)}/id-document-type`);
    url.searchParams.set('agentId', btoa(agentId));

    return this.http.get(url.toString(), options).pipe(
      map((response) => {
        return responseToObject<{type: IdDocumentType}>(response as HttpResponse<string>).type;
      }),
      catchError(handleHttpError),
    );
  }

  updateSubmitter(
    sessionId: string,
    proposalId: string,
    submitter: UserInfo,
    isReferral: boolean,
    versionHash?: string,
  ): Observable<Proposal> {
    const options = defaultOptions(sessionId, {appName: this.environment.app, versionHash}).buildOptions();

    const url = new URL(`${this.basePath}/${btoa(proposalId)}/submitter`);
    if (isReferral) {
      url.searchParams.set('isReferral', 'true');
    }

    return this.http.put(url.toString(), submitter, options).pipe(
      map((response) => responseToObject<Proposal>(response as HttpResponse<string>)),
      catchError(handleHttpError),
    );
  }

  updateHealthAnswers(
    sessionId: string,
    proposalId: string,
    answers: HealthAnswers,
    versionHash?: string,
  ): Observable<Proposal> {
    const body = {...answers};
    if (answers.birthday) {
      body.birthday = getDateWithoutOffset(answers.birthday);
    }
    const options = defaultOptions(sessionId, {appName: this.environment.app, versionHash}).buildOptions();
    const url = new URL(`${this.basePath}/${btoa(proposalId)}/health-answers`);

    return this.http.put(url.toString(), body, options).pipe(
      map((response) => responseToObject<Proposal>(response as HttpResponse<string>)),
      catchError(handleHttpError),
    );
  }

  confirmStep(
    sessionId: string,
    proposalId: string,
    confirmationStep: ConfirmationStep,
    versionHash: string,
  ): Observable<Proposal> {
    const options = defaultOptions(sessionId, {appName: this.environment.app, versionHash}).buildOptions();
    const url = new URL(`${this.basePath}/${btoa(proposalId)}/steps/${confirmationStep}/confirm`);

    return this.http.post(url.toString(), {}, options).pipe(
      map((response) => responseToObject<Proposal>(response as HttpResponse<string>)),
      catchError(handleHttpError),
    );
  }

  submit(sessionId: string, proposalId: string): Observable<Proposal> {
    const options = defaultOptions(sessionId, {appName: this.environment.app}).buildOptions();
    const url = new URL(`${this.basePath}/${btoa(proposalId)}/submit`);

    return this.http.put(url.toString(), {}, options).pipe(
      map((response) => responseToObject<Proposal>(response as HttpResponse<string>)),
      catchError(handleHttpError),
    );
  }

  updateProtection(
    sessionId: string,
    proposalId: string,
    coverage: Coverage,
    versionHash?: string,
  ): Observable<Proposal> {
    const options = defaultOptions(sessionId, {appName: this.environment.app, versionHash}).buildOptions();
    const url = new URL(`${this.basePath}/${btoa(proposalId)}/protection`);

    return this.http.put(url.toString(), coverage, options).pipe(
      map((response) => responseToObject<Proposal>(response as HttpResponse<string>)),
      catchError(handleHttpError),
    );
  }

  updateBeneficiaries(
    sessionId: string,
    proposalId: string,
    beneficiaries: Beneficiary[],
  ): Observable<Proposal> {
    const body = {beneficiaries: [...beneficiaries]};
    body.beneficiaries.forEach((b) => {
      if (b.birthday) {
        b.birthday = getDateWithoutOffset(b.birthday);
      }
      if (b.trustee?.birthday) {
        b.trustee.birthday = getDateWithoutOffset(b.trustee.birthday);
      }
    });

    const options = defaultOptions(sessionId, {appName: this.environment.app}).buildOptions();
    const url = new URL(`${this.basePath}/${btoa(proposalId)}/beneficiaries`);

    return this.http.put(url.toString(), body, options).pipe(
      map((response) => responseToObject<Proposal>(response as HttpResponse<string>)),
      catchError(handleHttpError),
    );
  }

  updateJobCategoryId(sessionId: string, proposalId: string, jobCategoryId: number): Observable<Proposal> {
    const options = defaultOptions(sessionId, {appName: this.environment.app}).buildOptions();
    const url = new URL(`${this.basePath}/${btoa(proposalId)}/job-category-id`);

    return this.http.put(url.toString(), {jobCategoryId}, options).pipe(
      map((response) => responseToObject<Proposal>(response as HttpResponse<string>)),
      catchError(handleHttpError),
    );
  }

  addReplacementNotice(sessionId: string, proposalId: string, file: File): Observable<Proposal> {
    const options = new HttpOptionsBuilder()
      .withApiKey(sessionId)
      .withHeader(APP_NAME_HEADER_KEY, this.environment.app)
      .buildOptions();
    const url = new URL(`${this.basePath}/${btoa(proposalId)}/replacement-notice`);
    const body = new FormData();
    body.append('file', file);

    return this.http.post(url.toString(), body, options).pipe(
      map((response) => responseToObject<Proposal>(response as HttpResponse<string>)),
      catchError(handleHttpError),
    );
  }

  removeReplacementNotice(sessionId: string, proposalId: string): Observable<Proposal> {
    const options = defaultOptions(sessionId, {appName: this.environment.app}).buildOptions();
    const url = new URL(`${this.basePath}/${btoa(proposalId)}/replacement-notice`);

    return this.http.delete(url.toString(), options).pipe(
      map((response) => responseToObject<Proposal>(response as HttpResponse<string>)),
      catchError(handleHttpError),
    );
  }

  updateIdDocument(
    sessionId: string,
    proposalId: string,
    idDocument: IdDocument,
    versionHash?: string,
  ): Observable<Proposal> {
    const options = defaultOptions(sessionId, {appName: this.environment.app, versionHash}).buildOptions();
    const url = new URL(`${this.basePath}/${btoa(proposalId)}/id-document`);

    return this.http.put(url.toString(), idDocument, options).pipe(
      map((response) => responseToObject<Proposal>(response as HttpResponse<string>)),
      catchError(handleHttpError),
    );
  }

  updateAgentDisclosures(
    sessionId: string,
    proposalId: string,
    clientAgentAffiliationTime: ClientAgentAffiliationTime,
    isAgentDisclosuresAccepted?: boolean,
  ): Observable<Proposal> {
    const options = defaultOptions(sessionId, {appName: this.environment.app}).buildOptions();
    const url = new URL(`${this.basePath}/${btoa(proposalId)}/agent-disclosures`);

    return this.http
      .put(
        url.toString(),
        {
          clientAgentAffiliationTime,
          isAccepted: isAgentDisclosuresAccepted,
        },
        options,
      )
      .pipe(
        map((response) => responseToObject<Proposal>(response as HttpResponse<string>)),
        catchError(handleHttpError),
      );
  }

  updateNote(sessionId: string, proposalId: string, note: string | undefined): Observable<Proposal> {
    const options = defaultOptions(sessionId, {appName: this.environment.app}).buildOptions();
    const url = new URL(`${this.basePath}/${btoa(proposalId)}/note`);

    return this.http.put(url.toString(), {note}, options).pipe(
      map((response) => responseToObject<Proposal>(response as HttpResponse<string>)),
      catchError(handleHttpError),
    );
  }

  send(sessionId: string, proposalId: string): Observable<Proposal> {
    const options = defaultOptions(sessionId, {appName: this.environment.app}).buildOptions();
    const url = new URL(`${this.basePath}/${btoa(proposalId)}/send`);

    return this.http.post(url.toString(), {}, options).pipe(
      map((response) => responseToObject<Proposal>(response as HttpResponse<string>)),
      catchError(handleHttpError),
    );
  }

  delete(sessionId: string, proposalId: string): Observable<undefined> {
    const options = defaultOptions(sessionId, {appName: this.environment.app}).buildOptions();
    const url = new URL(`${this.basePath}/${btoa(proposalId)}`);

    return this.http.delete(url.toString(), options).pipe(
      map(() => undefined),
      catchError(handleHttpError),
    );
  }

  createStripeSetupIntent(
    sessionId: string,
    proposalId: string,
  ): Observable<{clientSecret: string; proposal: Proposal}> {
    const options = defaultOptions(sessionId).buildOptions();
    const url = new URL(`${this.basePath}/${btoa(proposalId)}/stripe-setup-intent`);

    return this.http.post(url.toString(), undefined, options).pipe(
      map((response) =>
        responseToObject<{clientSecret: string; proposal: Proposal}>(response as HttpResponse<string>),
      ),
      catchError(handleHttpError),
    );
  }

  copyToBrokerage(sessionId: string, proposalId: string): Observable<undefined> {
    const options = defaultOptions(sessionId).buildOptions();
    const url = new URL(`${this.basePath}/${btoa(proposalId)}/copy-to-brokerage`);

    return this.http.post(url.toString(), undefined, options).pipe(
      map(() => undefined),
      catchError(handleHttpError),
    );
  }
}
